import { Box, Icon, Link, theme } from '@everlywell/ui-kit';
import { CaretRight } from '@phosphor-icons/react';
import { Link as GatsbyLink } from 'gatsby';
import React from 'react';

export type HorizontalProductCardV2Props = {
  /**
   * Image slot for the card. Prefer the Gatsby Image component.
   */
  image: React.ReactNode;
  /**
   * Pricing slot for the card. It should use the CreditValue and PriceV2 components.
   * */
  pricing: React.ReactNode;
  /**
   * Children to be passed inside the component, Generally the product name and a short description
   */
  children: React.ReactNode | React.ReactNode[];
  /**
   * Url to where the card should link to. It usually is the product page.
   * If not provided, the card will not be clickable.
   */
  linkTo?: string;
  /**
   * Optional color of the circle behind product image. Defaults to moonlight.wash
   */
  circleBackgroundColor?: string;
  /**
   * Optional function to be called when the card is clicked
   */
  onClick?: () => void;
};

/**
 * Horizontal Product Card V2 component
 * Used to display a mini product card
 * Uses the Everlywell UI Kit components
 *
 * @param {HorizontalProductCardV2Props} props
 *
 */
function HorizontalProductCardV2({
  image,
  pricing,
  children,
  linkTo,
  circleBackgroundColor,
  onClick,
}: HorizontalProductCardV2Props): JSX.Element {
  return (
    <Link
      as={GatsbyLink}
      to={linkTo}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      height="100%"
      backgroundColor={theme.colors.tints.white}
      padding={['3', '5']}
      borderRadius={'xl'} // 12px
      _hover={{
        textDecoration: 'none',
      }}
      flex={['unset', '1']}
      marginTop={['2', '2', 'unset']}
      onClick={onClick}
    >
      {/* First Box */}
      <Box>
        {/* Content passed inside the component, Generally the product name */}
        <Box>{children ? children : null}</Box>

        {/* This holds pricing */}
        <Box flex="1" display="flex" alignItems="center" marginTop="1">
          {pricing ? pricing : null}
        </Box>
      </Box>

      {/* Second Box */}
      <Box flex="1" display="flex" justifyContent="end" position="relative">
        <Box
          borderRadius="50%"
          width={['64px', '84px']}
          height={['64px', '84px']}
          background={circleBackgroundColor || 'moonlight.wash'}
          position="absolute"
          zIndex="0"
        />
        {image}
      </Box>

      <Box display={[null, null, 'none']} marginLeft="4">
        <Icon as={CaretRight} color="viridian.base" size="sm" />
      </Box>
    </Link>
  );
}

export default HorizontalProductCardV2;
